/**
 * FlickityNative — Drop-in vanilla JS replacement for Flickity v2
 *
 * Usage:
 *   var flkty = new FlickityNative('.carousel', {
 *     wrapAround: true, freeScroll: true, pageDots: true,
 *     contain: true, draggable: true, prevNextButtons: true
 *   });
 *   flkty.previous();  flkty.next();  flkty.select(2);
 *
 * Also accepts data-flickity='{ ... }' attribute (JSON) for declarative init.
 */
(function () {
  'use strict';

  function FlickityNative(selector, options) {
    var el = typeof selector === 'string' ? document.querySelector(selector) : selector;
    if (!el) return;

    // check for data-flickity attribute
    if (!options) {
      var attr = el.getAttribute('data-flickity');
      if (attr) {
        try { options = JSON.parse(attr); } catch (e) { options = {}; }
      } else {
        options = {};
      }
    }

    var defaults = {
      cellAlign: 'center',
      wrapAround: false,
      freeScroll: false,
      imagesLoaded: false,
      pageDots: true,
      contain: true,
      draggable: true,
      prevNextButtons: true,
      groupCells: false,
      selectedAttraction: 0.025,
      friction: 0.28,
      freeScrollFriction: 0.075
    };

    var o = {};
    for (var k in defaults) o[k] = defaults[k];
    for (var k2 in options) o[k2] = options[k2];

    // ── state ──
    var viewport, slider;
    var cells = [];
    var selectedIndex = 0;
    var isAnimating = false;
    var translateX = 0;

    // drag state
    var isDragging = false;
    var dragStartX = 0;
    var dragStartY = 0;
    var dragAxis = null; // 'x' | 'y' | null — decided on first move (touch axis lock)
    var dragCurrentX = 0;
    var dragStartTranslate = 0;
    var velocity = 0;
    var lastDragX = 0;
    var lastDragTime = 0;
    var rafId = null;
    var transitionHandler = null;
    var transitionTimer = null;

    // ── build ──
    function build() {
      el.classList.add('flickity-enabled');

      var children = Array.prototype.slice.call(el.children);

      viewport = document.createElement('div');
      viewport.className = 'flickity-viewport';
      viewport.style.position = 'relative';
      viewport.style.overflow = 'hidden';
      viewport.style.touchAction = 'pan-y';

      slider = document.createElement('div');
      slider.className = 'flickity-slider';
      slider.style.position = 'relative';
      slider.style.display = 'flex';
      slider.style.willChange = 'transform';

      children.forEach(function (child) {
        child.classList.add('flickity-cell');
        slider.appendChild(child);
        cells.push(child);
      });

      viewport.appendChild(slider);
      el.innerHTML = '';
      el.appendChild(viewport);

      if (o.prevNextButtons) buildButtons();
      if (o.pageDots) buildDots();

      updateSelectedClass();
      bindEvents();

      if (o.imagesLoaded) {
        var imgs = el.querySelectorAll('img');
        var loaded = 0;
        var total = imgs.length;
        if (total === 0) return;
        imgs.forEach(function (img) {
          if (img.complete) {
            loaded++;
            if (loaded === total) layout();
          } else {
            img.addEventListener('load', function () {
              loaded++;
              if (loaded === total) layout();
            });
          }
        });
      }
    }

    function buildButtons() {
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'flickity-button flickity-prev-next-button previous';
      prev.setAttribute('aria-label', 'Previous');
      prev.innerHTML = '<svg viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow"></path></svg>';
      prev.addEventListener('click', function (e) { e.preventDefault(); previous(); });

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'flickity-button flickity-prev-next-button next';
      next.setAttribute('aria-label', 'Next');
      next.innerHTML = '<svg viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180)"></path></svg>';
      next.addEventListener('click', function (e) { e.preventDefault(); nextSlide(); });

      el.appendChild(prev);
      el.appendChild(next);
    }

    var dotsContainer;
    function buildDots() {
      dotsContainer = document.createElement('ol');
      dotsContainer.className = 'flickity-page-dots';
      el.appendChild(dotsContainer);
      renderDots();
    }

    function renderDots() {
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';
      for (var i = 0; i < cells.length; i++) {
        var dot = document.createElement('li');
        dot.className = 'dot' + (i === selectedIndex ? ' is-selected' : '');
        (function (idx) {
          dot.addEventListener('click', function () { select(idx); });
        })(i);
        dotsContainer.appendChild(dot);
      }
    }

    // ── layout ──
    function layout() {
      // nothing to recalculate — cells use their natural / CSS-set widths
    }

    function getCellPositions() {
      var positions = [];
      var x = 0;
      for (var i = 0; i < cells.length; i++) {
        var cell = cells[i];
        var w = cell.offsetWidth;
        var mr = parseInt(window.getComputedStyle(cell).marginRight) || 0;
        positions.push({ left: x, width: w, center: x + w / 2 });
        x += w + mr;
      }
      return positions;
    }

    function getTargetX(idx) {
      var positions = getCellPositions();
      if (!positions[idx]) return 0;
      var viewW = viewport.offsetWidth;

      if (o.cellAlign === 'center') {
        return -(positions[idx].center - viewW / 2);
      } else {
        return -positions[idx].left;
      }
    }

    function setTranslate(x, animate) {
      translateX = x;
      if (animate) {
        slider.style.transition = 'transform 0.4s ease';
      } else {
        slider.style.transition = 'none';
      }
      slider.style.transform = 'translate3d(' + x + 'px, 0, 0)';
    }

    // Read the slider's *rendered* X offset (mid-transition value), so a gesture
    // that interrupts an animation can freeze the slider exactly where it is.
    function getRenderedX() {
      var t = window.getComputedStyle(slider).transform;
      if (!t || t === 'none') return translateX;
      var m = t.match(/matrix(?:3d)?\(([^)]+)\)/);
      if (!m) return translateX;
      var v = m[1].split(',').map(parseFloat);
      return v.length === 16 ? v[12] : v[4];
    }

    function clearAnimation() {
      if (transitionHandler) {
        slider.removeEventListener('transitionend', transitionHandler);
        slider.removeEventListener('transitioncancel', transitionHandler);
        transitionHandler = null;
      }
      if (transitionTimer) { clearTimeout(transitionTimer); transitionTimer = null; }
      isAnimating = false;
    }

    // Stop an in-flight slide animation and pin the slider at its current
    // rendered position, so the next drag/select starts from reality.
    function freezeAnimation() {
      if (!isAnimating) return;
      var x = getRenderedX();
      clearAnimation();
      setTranslate(x, false);
    }

    function updateSelectedClass() {
      cells.forEach(function (c, i) {
        c.classList.toggle('is-selected', i === selectedIndex);
      });
      renderDots();
    }

    // ── navigation ──
    function select(idx, isWrapping) {
      // A select() during an in-flight animation (fast double swipe, rapid
      // arrow/dot clicks) interrupts it and continues from the rendered
      // position instead of being dropped.
      if (isAnimating) freezeAnimation();

      if (o.wrapAround) {
        if (idx < 0) idx = cells.length - 1;
        if (idx >= cells.length) idx = 0;
      } else {
        idx = Math.max(0, Math.min(idx, cells.length - 1));
      }

      selectedIndex = idx;

      var target = getTargetX(idx);

      // contain: prevent scrolling past the end
      if (o.contain && !o.wrapAround) {
        var positions = getCellPositions();
        var lastPos = positions[positions.length - 1];
        var maxScroll = -(lastPos.left + lastPos.width - viewport.offsetWidth);
        if (maxScroll > 0) maxScroll = 0;
        if (target < maxScroll) target = maxScroll;
        if (target > 0) target = 0;
      }

      // If already at the target position, skip animation entirely.
      // Without this guard, setTranslate would apply a CSS transition but the
      // transform value wouldn't change, so transitionend would never fire —
      // leaving isAnimating=true permanently and blocking all future select() calls.
      if (target === translateX) {
        updateSelectedClass();
        return;
      }

      clearAnimation();
      isAnimating = true;
      setTranslate(target, true);

      // transitionend does not fire when a transition is interrupted (a new
      // transform, or transition:none from a drag) — that used to leave
      // isAnimating stuck at true and dead-lock the slider after a fast/double
      // swipe. Listen for transitioncancel too, and keep a safety timer.
      transitionHandler = function () { clearAnimation(); };
      slider.addEventListener('transitionend', transitionHandler);
      slider.addEventListener('transitioncancel', transitionHandler);
      transitionTimer = setTimeout(clearAnimation, 500);

      updateSelectedClass();
    }

    function nextSlide() {
      select(selectedIndex + 1);
    }

    function previous() {
      select(selectedIndex - 1);
    }

    // ── drag ──
    function onPointerDown(e) {
      if (!o.draggable) return;
      if (e.button && e.button !== 0) return; // left click only
      freezeAnimation();
      isDragging = true;
      dragAxis = null;
      dragStartX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
      dragStartY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
      dragCurrentX = dragStartX;
      dragStartTranslate = translateX;
      lastDragX = dragStartX;
      lastDragTime = Date.now();
      velocity = 0;
      slider.style.transition = 'none';
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      var x = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      var y = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;

      // Axis lock (touch): decide ONCE, after ~6px of movement, whether this
      // gesture is a horizontal swipe or a vertical page scroll — never both.
      //  - vertical  → release the slider (snap back) and let the page scroll.
      //  - horizontal → own the gesture: preventDefault() every touchmove so the
      //    browser never starts a vertical pan, even if the finger drifts.
      // The viewport has touch-action: pan-y, so the browser may itself have
      // already committed to a vertical scroll (touchmove is then not
      // cancelable); in that case we defer to it and release, so the decision
      // is always exclusive whichever side made it first.
      var isTouch = e.type === 'touchmove';
      if (dragAxis === null) {
        var adx = Math.abs(x - dragStartX);
        var ady = Math.abs(y - dragStartY);
        if (adx < 6 && ady < 6) return;
        var browserIsScrolling = isTouch && e.cancelable === false;
        dragAxis = (ady > adx || browserIsScrolling) ? 'y' : 'x';
        if (dragAxis === 'y') {
          isDragging = false;
          select(selectedIndex);
          return;
        }
      }
      if (isTouch && e.cancelable) e.preventDefault();
      dragCurrentX = x;

      var now = Date.now();
      var dt = now - lastDragTime;
      if (dt > 0) {
        velocity = (x - lastDragX) / dt;
      }
      lastDragX = x;
      lastDragTime = now;

      var diff = dragCurrentX - dragStartX;
      slider.style.transform = 'translate3d(' + (dragStartTranslate + diff) + 'px, 0, 0)';
      translateX = dragStartTranslate + diff;
    }

    function onPointerUp() {
      if (!isDragging) return;
      isDragging = false;

      var diff = dragCurrentX - dragStartX;

      if (o.freeScroll) {
        // momentum scroll
        var target = translateX + velocity * 200;

        if (o.contain) {
          var positions = getCellPositions();
          var lastPos = positions[positions.length - 1];
          var maxScroll = -(lastPos.left + lastPos.width - viewport.offsetWidth);
          if (maxScroll > 0) maxScroll = 0;
          if (target < maxScroll) target = maxScroll;
          if (target > 0) target = 0;
        }

        setTranslate(target, true);

        // find closest cell
        var positions2 = getCellPositions();
        var closestIdx = 0;
        var closestDist = Infinity;
        for (var i = 0; i < positions2.length; i++) {
          var cellCenter = positions2[i].center;
          var viewCenter = -target + viewport.offsetWidth / 2;
          var dist = Math.abs(cellCenter - viewCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestIdx = i;
          }
        }
        selectedIndex = closestIdx;
        updateSelectedClass();
      } else {
        // A swipe counts if it travelled 15% of the viewport OR was a quick
        // flick (>= 0.35 px/ms with at least 20px of travel), so a short fast
        // swipe still advances instead of snapping back.
        var threshold = viewport.offsetWidth * 0.15;
        var isFlick = Math.abs(velocity) >= 0.35 && Math.abs(diff) >= 20;
        if (diff < -threshold || (isFlick && diff < 0)) {
          nextSlide();
        } else if (diff > threshold || (isFlick && diff > 0)) {
          previous();
        } else {
          select(selectedIndex);
        }
      }
    }

    // ── events ──
    function bindEvents() {
      viewport.addEventListener('touchstart', onPointerDown, { passive: true });
      // touchmove must be non-passive so a horizontal swipe can preventDefault()
      // the page scroll (see axis lock in onPointerMove).
      viewport.addEventListener('touchmove', onPointerMove, { passive: false });
      viewport.addEventListener('touchend', onPointerUp);
      viewport.addEventListener('touchcancel', onPointerUp);

      viewport.addEventListener('mousedown', onPointerDown);
      document.addEventListener('mousemove', onPointerMove);
      document.addEventListener('mouseup', onPointerUp);

      viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

      // click prevention during drag
      viewport.addEventListener('click', function (e) {
        if (Math.abs(dragCurrentX - dragStartX) > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          select(selectedIndex);
        }, 200);
      });
    }

    // ── public API ──
    this.select = select;
    this.next = nextSlide;
    this.previous = previous;
    this.selectedIndex = function () { return selectedIndex; };
    this.cells = cells;

    this.resize = function () {
      // Reset isAnimating in case a CSS transition was interrupted by the
      // gallery being hidden (display:none). When that happens, transitionend
      // never fires, leaving isAnimating=true and blocking all future select()
      // calls. Resetting here and repositioning instantly (animate=false) lets
      // the slider recover cleanly every time the gallery becomes visible again.
      isAnimating = false;
      var target = getTargetX(selectedIndex);
      setTranslate(target, false);
      updateSelectedClass();
    };

    this.destroy = function () {
      if (rafId) cancelAnimationFrame(rafId);
      el.innerHTML = '';
      cells.forEach(function (c) {
        c.classList.remove('flickity-cell', 'is-selected');
        el.appendChild(c);
      });
      el.classList.remove('flickity-enabled');
      el._flkty = null;
      el.flickity = null;
    };

    // init
    build();

    // Live options object (mirrors Flickity's `flkty.options`); onPointerDown reads
    // o.draggable at event time, so flipping flkty.options.draggable takes effect
    // immediately — used by the PDP gallery to toggle swipe across the mobile breakpoint.
    this.options = o;

    // Store instance on element for external retrieval (mirrors real Flickity API)
    el._flkty = this;
    el.flickity = this;
  }

  // static .data(element) — returns existing instance attached to element (Flickity v2 compat)
  FlickityNative.data = function (el) {
    if (typeof el === 'string') el = document.querySelector(el);
    if (!el) return null;
    return el._flickityNative || el.flickity || null;
  };

  // expose globally
  window.FlickityNative = FlickityNative;

  // shim for `new Flickity(...)` calls
  window.Flickity = window.Flickity || FlickityNative;

  // auto-init elements with data-flickity attribute
  document.addEventListener('DOMContentLoaded', function () {
    var els = document.querySelectorAll('[data-flickity]');
    els.forEach(function (el) {
      if (el._flickityNative) return;
      el._flickityNative = new FlickityNative(el);
    });
  });
})();
